import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../api';
import DashboardLayout from '../../../Components/Dashboard/DashboardLayout';
import './AdminSuite.css';

const initialFormData = {
  name: '',
  company: '',
  role: '',
  quote: '',
  avatar: '',
  rating: 5,
  sort_order: 0,
  published: true,
};

const AdminTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/testimonials');
      setTestimonials(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...formData,
        rating: Number(formData.rating),
        sort_order: Number(formData.sort_order),
      };

      if (editingId) {
        await api.put(`/admin/testimonials/${editingId}`, payload);
        toast.success('Testimonial updated');
      } else {
        await api.post('/admin/testimonials', payload);
        toast.success('Testimonial created');
      }

      resetForm();
      loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = testimonial => {
    setEditingId(testimonial.id);
    setFormData({
      name: testimonial.name || '',
      company: testimonial.company || '',
      role: testimonial.role || '',
      quote: testimonial.quote || '',
      avatar: testimonial.avatar || '',
      rating: testimonial.rating || 5,
      sort_order: testimonial.sortOrder || 0,
      published: Boolean(testimonial.published),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const togglePublished = async testimonial => {
    try {
      await api.put(`/admin/testimonials/${testimonial.id}`, {
        published: !testimonial.published,
      });
      toast.success(testimonial.published ? 'Testimonial unpublished' : 'Testimonial published');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial status');
    }
  };

  const deleteTestimonial = async id => {
    if (!window.confirm('Delete this testimonial?')) {
      return;
    }

    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success('Testimonial deleted');
      if (editingId === id) {
        resetForm();
      }
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4 admin-suite">
        <div className="admin-suite-header">
          <div>
            <h3 className="admin-suite-title">Testimonials</h3>
            <p className="admin-suite-subtitle">
              Manage the client testimonials displayed on the landing page.
            </p>
          </div>
        </div>

        <div className="admin-card mb-4">
          <div className="card-body">
            <h5 className="admin-section-title mb-3">
              {editingId ? 'Edit testimonial' : 'Add testimonial'}
            </h5>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Client name</label>
                  <input
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Company</label>
                  <input
                    className="form-control"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Role</label>
                  <input
                    className="form-control"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="e.g. Founder"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Rating</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    max="5"
                    name="rating"
                    value={formData.rating}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Sort order</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Quote</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    name="quote"
                    value={formData.quote}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-8">
                  <label className="form-label">Avatar URL</label>
                  <input
                    className="form-control"
                    name="avatar"
                    value={formData.avatar}
                    onChange={handleChange}
                    placeholder="https://example.com/avatar.jpg"
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="testimonial-published"
                      name="published"
                      checked={formData.published}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="testimonial-published">
                      Publish on landing page
                    </label>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2 mt-4">
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update testimonial' : 'Add testimonial'}
                </button>
                {editingId && (
                  <button className="btn btn-outline-secondary" type="button" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="admin-table-wrap admin-card">
          <div className="admin-table-head" style={{ gridTemplateColumns: '1.4fr 1.5fr 0.8fr 0.9fr 1.2fr' }}>
            <div>Client</div>
            <div>Quote</div>
            <div>Rating</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {loading && <div className="p-3 text-muted">Loading…</div>}

          {!loading && testimonials.length === 0 && (
            <div className="p-3 text-muted">No testimonials found. Add the first one above.</div>
          )}

          {!loading && testimonials.map(testimonial => (
            <div
              key={testimonial.id}
              className="admin-table-row"
              style={{ gridTemplateColumns: '1.4fr 1.5fr 0.8fr 0.9fr 1.2fr' }}
            >
              <div>
                <strong>{testimonial.name}</strong>
                <div className="text-muted small">{testimonial.role || 'Client'} • {testimonial.company}</div>
                <div className="text-muted small">Order: {testimonial.sortOrder}</div>
              </div>
              <div className="text-muted small">
                {testimonial.quote.length > 120 ? `${testimonial.quote.slice(0, 120)}…` : testimonial.quote}
              </div>
              <div>{'★'.repeat(testimonial.rating || 5)}</div>
              <div>
                <span className={`admin-pill ${testimonial.published ? 'published' : 'draft'}`}>
                  {testimonial.published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="admin-actions">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => togglePublished(testimonial)}
                >
                  {testimonial.published ? 'Unpublish' : 'Publish'}
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => startEdit(testimonial)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteTestimonial(testimonial.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminTestimonialsPage;